import {
  Box,
  Breadcrumbs,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import BulkMessagingToolbar from 'components/@home/@documents/BulkMessaging/BulkMessagingToolbar';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import DotsVerticalIcon from 'mdi-react/DotsVerticalIcon';
import DownloadIcon from 'mdi-react/DownloadIcon';
import PlayIcon from 'mdi-react/PlayIcon';
import { string } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router';
import { HR_BOT_APP_ID } from 'services/constants';
import appsAction from 'store/app/entities/apps/action';
import {
  useListBulkMessagingQuery,
  useResumeBulkMessagingMutation,
} from 'store/app/entities/SharedFilesSlice';
import alertActions from 'store/app/ui/Alert/action';
import employeesWithUser from 'store/selectors/employeesWithUser';
import isSuperAdminSelector from 'store/selectors/isSuperAdmin';
import arrayToObject from 'utils/arrayToObject';
import downloadBulkMessagingZip from 'utils/downloadBulkMessagingZip';
import { I18n } from 'utils/i18n';
import ms from 'ms';
import renderDateTimeCell from 'utils/renderDateTimeCell';

const BulkMessaging = ({ folder }) => {
  const employees = useSelector(employeesWithUser);
  const employeesByUser = arrayToObject(employees, 'user._id');
  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const { id } = useParams();
  const [perPage, setPerPage] = useState(10);
  const [sortModel, setSortModel] = useState(undefined);
  const [filterModel, setFilterModel] = useState(undefined);
  const [page, setPage] = useState(0);
  const search = filterModel?.quickFilterValues.join(' ');
  const location = useLocation();
  const dispatch = useDispatch();
  const app = useSelector(state => state.apps.apps?.[HR_BOT_APP_ID]);
  useEffect(() => {
    if (!app?._id) {
      dispatch(appsAction.load());
    }
  }, [app?._id, dispatch]);
  const {
    data: { bulkMessaging, totalCount, documentFormat } = {},
    isFetching,
    isUninitialized,
  } = useListBulkMessagingQuery({
    search,
    page: page + 1,
    perPage,
    id,
    sortBy: sortModel?.[0]?.field || 'createdAt',
    sort: sortModel?.[0]?.sort || 'desc',
  });
  const [resumeBulkMessaging, { isLoading: isResuming }] = useResumeBulkMessagingMutation();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedBulkMessagingId, setSelectedBulkMessagingId] = useState(null);
  const [bulkDownloadBusy, setBulkDownloadBusy] = useState(false);
  const bulkDownloadLockRef = useRef(false);

  const handleBulkDownload = useCallback(
    async (e, bulkMessagingId) => {
      e.preventDefault();
      e.stopPropagation();
      if (bulkDownloadLockRef.current) return;
      bulkDownloadLockRef.current = true;
      setBulkDownloadBusy(true);
      try {
        await downloadBulkMessagingZip({ formatId: id, bulkMessagingId });
      } catch (err) {
        dispatch(
          alertActions.show({
            variant: 'error',
            label: I18n.t('SignTracking.downloadBulkErrorTitle'),
            message: err?.message || I18n.t('SignTracking.downloadBulkErrorFallback'),
            autoHideDuration: ms('8 seconds'),
          }),
        );
      } finally {
        bulkDownloadLockRef.current = false;
        setBulkDownloadBusy(false);
      }
    },
    [dispatch, id],
  );

  const handleMenuOpen = (event, bulkMessagingId) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedBulkMessagingId(bulkMessagingId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedBulkMessagingId(null);
  };

  const handleResume = async () => {
    if (!selectedBulkMessagingId) return;
    handleMenuClose();
    try {
      const result = await resumeBulkMessaging({
        id,
        bulkMessagingId: selectedBulkMessagingId,
      }).unwrap();
      dispatch(
        alertActions.show({
          variant: 'success',
          label: I18n.t('BulkMessaging.Resume.Success') || 'Bulk Messaging Resumed',
          message:
            I18n.t('BulkMessaging.Resume.SuccessMessage', {
              count: result?.data?.pendingEnqueued || 0,
            }) || `${result?.data?.pendingEnqueued || 0} pending records re-enqueued`,
          autoHideDuration: ms('5 seconds'),
        }),
      );
    } catch (error) {
      dispatch(
        alertActions.show({
          variant: 'error',
          label: I18n.t('BulkMessaging.Resume.Error') || 'Error',
          message:
            error?.data?.message ||
            I18n.t('BulkMessaging.Resume.ErrorMessage') ||
            'Failed to resume bulk messaging',
          autoHideDuration: ms('5 seconds'),
        }),
      );
    }
  };
  const columns = [
    {
      field: 'createdAt',
      flex: 1,
      editable: false,
      renderCell: renderDateTimeCell,
      headerName: I18n.t('BulkMessaging.Table.createdAt'),
    },
    {
      field: 'user',
      flex: 1,
      editable: false,
      renderCell: params =>
        params.row.employeeName ||
        employeesByUser?.[params.value]?.name ||
        I18n.t('BulkMessaging.Not found'),
      headerName: I18n.t('BulkMessaging.Table.user'),
    },
    {
      field: 'sent',
      type: 'number',
      editable: false,
      maxWidth: 150,
      marginRight: 100,
      headerName: I18n.t('BulkMessaging.Table.sent'),
    },
    {
      field: 'error',
      type: 'number',
      editable: false,
      maxWidth: 150,
      marginRight: 100,
      headerName: I18n.t('BulkMessaging.Table.error'),
    },
    {
      field: 'total',
      type: 'number',
      editable: false,
      maxWidth: 150,
      marginRight: 100,
      headerName: I18n.t('BulkMessaging.Table.total'),
    },
    {
      field: 'status',
      flex: 1,
      editable: false,
      headerName: I18n.t('BulkMessaging.Table.status') || 'Status',
      valueFormatter: value => I18n.t(`BulkMessaging.Status.${value}`) || value,
    },
    {
      field: 'download',
      editable: false,
      sortable: false,
      headerName: '',
      width: 70,
      renderCell: params => (
        <IconButton
          size="small"
          disabled={bulkDownloadBusy}
          onClick={e => handleBulkDownload(e, params.row._id)}
          aria-label={I18n.t('BulkMessaging.Download')}
        >
          <DownloadIcon />
        </IconButton>
      ),
    },
    ...(isSuperAdmin
      ? [
          {
            field: 'actions',
            editable: false,
            sortable: false,
            headerName: '',
            renderCell: params => {
              const { status } = params.row;
              const shouldShowResume = status === 'PENDING' || status === 'IN_PROGRESS';
              if (!shouldShowResume) {
                return null;
              }
              return (
                <IconButton
                  sx={{ alignSelf: 'flex-end' }}
                  size="small"
                  onClick={e => handleMenuOpen(e, params.row._id)}
                  aria-label={I18n.t('BulkMessaging.Actions.Menu') || 'Actions'}
                >
                  <DotsVerticalIcon />
                </IconButton>
              );
            },
          },
        ]
      : []),
  ];
  return (
    <Grid
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <BulkMessagingToolbar documentFormat={documentFormat} />
      <Box
        sx={{
          p: 2,
          pb: 0,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            to={{
              pathname: `/home/documents/${folder}/`,
              search: location.state?.search,
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: 'normal' }}>
              {I18n.t(`DocumentsScene.Folders.${folder}`)}
            </Typography>
          </Link>
          <Typography variant="h3">{documentFormat?.name}</Typography>
        </Breadcrumbs>
        <OmmnioDataGrid
          loading={isFetching || isUninitialized}
          sx={{ marginTop: 2 }}
          columns={columns}
          rows={bulkMessaging}
          paginationMode="server"
          getRowId={r => r._id}
          rowCount={totalCount || 0}
          sortingMode="server"
          filterMode="server"
          page={page}
          sortModel={sortModel}
          filterModel={filterModel}
          onPageChange={setPage}
          onPageSizeChange={setPerPage}
          onFilterModelChange={setFilterModel}
          onSortModelChange={setSortModel}
          defaultSortModel={[{ field: 'createdAt', sort: 'desc' }]}
          updateHistory
          disableEvenOdd
        />
      </Box>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleResume} disabled={isResuming}>
          <ListItemIcon>
            <PlayIcon />
          </ListItemIcon>
          <Typography variant="inherit">
            {I18n.t('BulkMessaging.Resume.Button') || 'Reanudar'}
          </Typography>
        </MenuItem>
      </Menu>
    </Grid>
  );
};

BulkMessaging.propTypes = {
  folder: string.isRequired,
};

export default BulkMessaging;
